import { useBasePath } from '../../../lib/use-base-path';
import { useTranslation } from '../../../lib/i18n';
import { PickerSubscription, PickerTopup } from '../../../lib/plans';
import { activePremiumSubscription, isFreeSettingsPlan } from './settings-plan';
import { activePlanIsMaxPlus, activePlanTopup } from './max-plus';
import { fmtDate, planNameKeyOf } from './settings-shared';
import { useSettingsData } from './settings-data-context';

export interface SettingsPlanView {
  /** True while the plan call failed or is unavailable — surfaces hide the plan. */
  planUnavailable: boolean;
  /** The active subscription record (for display details), or undefined if none. */
  sub: PickerSubscription | undefined;
  /** Free vs paid — decided from the credits account's isFreePlan, like the sidebar. */
  isFree: boolean;
  /**
   * Whether we hold a Stripe-backed subscription of our own. Deliberately NOT `!isFree`: `isFree`
   * answers "does this account have a paid credit grant" (which a Wix subscription satisfies),
   * while this answers "is there a subscription of OURS". See PremiumPlan.hasOurSubscription.
   */
  hasOurSubscription: boolean;
  /**
   * True when the active paid plan is the top ("max") catalog tier — native's `isMaxPlus`. Keys the
   * plan card's CTA (Change vs Upgrade) and whether a top-up button shows (Figma 12094-64368).
   */
  isMaxPlus: boolean;
  /** The one-time top-up pack the current plan option offers (Max+ only today), or null. */
  topup: PickerTopup | null;
  packagesHref: string;
  /** Free tier: the resets/renews line if the credit period is known, else the upsell hint. */
  freeSubtitle: string;
  /** Subscribed: the paid plan's name (falls back to a generic label). */
  planName: string;
  renewDate: string;
  /** Subscribed: the renews/cancels line. */
  subscribedSubtitle: string;
  pendingCancel: boolean;
  pendingDowngrade: boolean;
  downgradeName: string | null;
  scheduledChangeDate: string | null;
}

// Every derived value the Plan card shows, computed ONCE and shared by SettingsDesktop
// (PlanCreditsCard) and SettingsMobile (PlanCard). The two used to re-derive all of this
// independently — cadence, renew date, pending flags, the subtitle —
// which is exactly how they drifted (desktop showed "Free" for a paid user). Each
// surface renders its own markup from these values; none of them recompute.
export function useSettingsPlanView(): SettingsPlanView {
  const basePath = useBasePath();
  const { t } = useTranslation();
  const { plan, credits } = useSettingsData();
  const { premium, plans, scheduled, failed, hasOurSubscription } = plan;

  // The free/paid decision comes from the credits account's isFreePlan (the sidebar's
  // signal), so Settings and the sidebar can't disagree; `sub` is only the record we
  // read plan details from when paid.
  const sub = activePremiumSubscription(premium);
  const isFree = isFreeSettingsPlan(credits.account?.isFreePlan, premium);
  const pendingCancel = scheduled?.pendingCancellation === true;
  const pendingDowngrade = scheduled?.hasPendingChange === true;
  const renewDate = sub ? fmtDate(sub.currentPeriodEnd) : '';
  const resetDateIso = credits.account?.resetDateIso ?? null;

  // Which tier, and whether it carries a top-up pack. Both read the account's catalog option, and both
  // live in `max-plus.ts` so the drawer credits card reaches the SAME answers — it is not inside the
  // settings data context and used to be able to derive neither.
  const isMaxPlus = activePlanIsMaxPlus(premium, plans);
  const topup = activePlanTopup(premium, plans);
  // Null (not a fallback label) when there is no scheduled downgrade or its plan left the catalog —
  // the surfaces render the whole "moving to …" line only when there is a name to put in it.
  const downgradeNameKey = planNameKeyOf(plans, scheduled?.pendingPlanId ?? null);

  return {
    planUnavailable: failed || premium === null,
    sub,
    isFree,
    hasOurSubscription,
    isMaxPlus,
    topup,
    packagesHref: `${basePath}/packages`,
    freeSubtitle: resetDateIso
      ? t('settings.plan.creditsRenews', { date: fmtDate(resetDateIso) })
      : t('settings.plan.freeHint'),
    // Paid: the plan's catalog name, or a generic label when the record is missing
    // or its plan isn't in the current catalog (a legacy grant). The catalog carries the
    // name as a key, so it is translated here rather than shipped English from the server.
    planName: t((sub && planNameKeyOf(plans, sub.planId)) ?? 'settings.plan.yourPlan'),
    renewDate,
    // "Renews {date}" — no cadence word (Figma 12094-64368 / native show just the date).
    subscribedSubtitle: pendingCancel
      ? t('settings.plan.cancelsOn', { date: fmtDate(scheduled?.cancellationDate) || t('settings.plan.periodEnd') })
      : renewDate
        ? t('settings.plan.creditsRenews', { date: renewDate })
        : '',
    pendingCancel,
    pendingDowngrade,
    downgradeName: downgradeNameKey ? t(downgradeNameKey) : null,
    scheduledChangeDate: scheduled?.scheduledChangeDate ?? null,
  };
}
